import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import useFetch from '@e3dc-react/shell/Hooks/useFetch'
import { isUndefined } from 'lodash'
import moment from '@e3dc-react/shell/Libs/moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DifferenceModel } from 'src/Elements/Dashboard/HistoryValuesModel'
import { ApiResult } from 'src/Libs/api'
import { getPowerMeterDifferences } from 'src/Libs/power-meters/api/power-meters.api'
import { ONE_MINUTE_MS } from 'src/Libs/system-data-handler/utils/helperFunctions'
import { useTypedSelector } from 'src/State/RootReducer'
import { PowermeterHardwareModel } from 'src/State/Storage/StorageModel'
import { SummaryTimeView } from '../types'
import { calculateFromBasedOnView, calculatePreviousFromBasedOnView, calculatePreviousToBasedOnView } from '../utils'

interface HookReturnType {
  differences?: DifferenceModel
  /** for example the differences of the previous and not the current week */
  previousDifferences?: DifferenceModel
  powermeterProductionValue: number
  previousPowermeterProductionValue: number
  isLoading: boolean
}

/**
 * Provides data to display the summary data (e.g. Co2 savings)
 *
 * @param period Time period for which to get the data
 * @param powermeterProducers
 */
export const useSummaryData = (period: SummaryTimeView, powermeterProducers?: PowermeterHardwareModel[]): HookReturnType => {
  const activeStorage = useTypedSelector(state => state.storage.activeStorage) || ''
  const [date, setDate] = useState<number>(Date.now())

  useEffect(() => {
    const intervalId = setInterval(() => {
      setDate(Date.now())
    }, ONE_MINUTE_MS)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  const previousFrom = useMemo(() => calculatePreviousFromBasedOnView(period), [period])
  const from = useMemo(() => calculateFromBasedOnView(period), [period])

  const to = useMemo(() => moment().add(1, 'day').endOf('day'), [])
  const previousTo = useMemo(() => calculatePreviousToBasedOnView(period, previousFrom), [period, previousFrom])

  const [differences, isDifferenceLoading] = useFetch<DifferenceModel>({
    url: `storages/${activeStorage}/history-values/difference?from=${from.valueOf()}&to=${to.valueOf()}&approach=true`,
    doNotFetch: !activeStorage,
    dependencies: [date],
  })

  const [previousDifferences, isPreviousDifferenceLoading] = useFetch<DifferenceModel>({
    url: `storages/${activeStorage}/history-values/difference?from=${previousFrom.valueOf()}&to=${previousTo.valueOf()}&approach=true`,
    doNotFetch: !activeStorage,
    dependencies: [date],
  })

  const authFetch = useAuthFetchWithResponse()

  const loadPowermeterDifferences = useCallback(
    async (from: number, to: number): Promise<ApiResult<DifferenceModel>[]> => {
      if (isUndefined(activeStorage)) return []
      const promises = powermeterProducers?.map(powermeterProducer =>
        getPowerMeterDifferences(powermeterProducer.id, powermeterProducer.serialno.toString(), from.valueOf(), to.valueOf(), authFetch)
      )
      if (isUndefined(promises)) return []
      return await Promise.all(promises)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeStorage, authFetch, powermeterProducers, date]
  )

  const [powermeterDifferences, setPowermeterDifferences] = useState<ApiResult<DifferenceModel>[]>([])
  const [isPowermeterDifferencesLoading, setIsPowermeterDifferencesLoading] = useState<boolean>(true)
  const [powermeterProductionValue, setPowermeterProductionValue] = useState<number>(0)

  const [previousPowermeterDifferences, setPreviousPowermeterDifferences] = useState<ApiResult<DifferenceModel>[]>([])
  const [previousPowermeterProductionValue, setPreviousPowermeterProductionValue] = useState<number>(0)

  useEffect(() => {
    loadPowermeterDifferences(from.valueOf(), to.valueOf())
      .then(data => {
        setPowermeterDifferences(data)
      })
      .catch(err => console.error(err))
      .finally(() => {
        setIsPowermeterDifferencesLoading(false)
      })

    loadPowermeterDifferences(previousFrom.valueOf(), previousTo.valueOf())
      .then(data => {
        setPreviousPowermeterDifferences(data)
      })
      .catch(err => console.error(err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [powermeterProducers, from, to, previousFrom, previousTo])

  useEffect(() => {
    if (powermeterDifferences) {
      const productionSum: number = powermeterDifferences.reduce((prev, curr) => {
        return prev + (curr?.value?.diff?.NetIn ?? 0)
      }, 0)
      setPowermeterProductionValue(productionSum)
    }
  }, [powermeterDifferences])

  useEffect(() => {
    if (previousPowermeterDifferences) {
      const productionSum: number = previousPowermeterDifferences.reduce((prev, curr) => {
        return prev + (curr?.value?.diff?.NetIn ?? 0)
      }, 0)
      setPreviousPowermeterProductionValue(productionSum)
    }
  }, [previousPowermeterDifferences])

  const isLoading = isDifferenceLoading || isPreviousDifferenceLoading || isPowermeterDifferencesLoading

  return {
    differences,
    previousDifferences,
    powermeterProductionValue,
    previousPowermeterProductionValue,
    isLoading,
  }
}
